// src/components/shipment/components/ShipmentInfo.tsx

import React from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Stack,
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress
} from '@mui/material';
import {
  Person,
  CalendarToday,
  Inventory,
  Speed,
  Assignment,
  InsertDriveFile,
  Download,
  QrCodeScanner
} from '@mui/icons-material';
import { Shipment, BoxProp as ShipmentBox, ShipmentStatus } from '../../../types/Shipment.types';

interface ShipmentInfoProps {
  shipment: Shipment;
  selectedBox?: ShipmentBox | null;
  variant?: 'compact' | 'detailed';
  onFileDownload?: (fileUrl: string, fileName?: string) => void;
  downloadingFile?: boolean;
}

const ShipmentInfo: React.FC<ShipmentInfoProps> = ({
  shipment,
  selectedBox,
  variant = 'compact',
  onFileDownload,
  downloadingFile = false
}) => {
  const handleFileDownload = (fileUrl: string, fileName?: string) => {
    if (onFileDownload) {
      onFileDownload(fileUrl, fileName);
    }
  };

  if (variant === 'detailed') {
    return (
      <Card sx={{ height: 'fit-content', bgcolor: 'grey.50' }}>
        <CardHeader
          title={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Assignment sx={{ color: 'primary.main' }} />
              <Typography variant="h6" fontWeight="bold">
                Shipment Information
              </Typography>
            </Stack>
          }
          sx={{ pb: 1 }}
        />
        <CardContent sx={{ pt: 2 }}>
          <Stack spacing={2}>
            {/* Supplier */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Person sx={{ color: 'primary.main', fontSize: 20 }} />
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Supplier
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {shipment.supplier?.name || shipment.supplierName}
                  </Typography>
                  {shipment.supplier?.id && (
                    <Typography variant="caption" color="text.secondary">
                      ID: {shipment.supplier.id}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </Box>

            {/* Reference Number */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <QrCodeScanner sx={{ color: 'info.main', fontSize: 20 }} />
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Reference Number
                  </Typography>
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ fontFamily: 'monospace' }}
                  >
                    {shipment.refNumber}
                  </Typography>
                </Box>
              </Stack>
            </Box>

            {/* Dates */}
            <Box
              sx={{
                p: 2,
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <CalendarToday sx={{ color: 'success.main', fontSize: 20 }} />
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight="medium"
                  >
                    Expected Date
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {new Date(shipment.expectedDate).toLocaleDateString()}
                  </Typography>
                  {shipment.actualArrivalDate && (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight="medium"
                        sx={{ mt: 1, display: 'block' }}
                      >
                        Actual Arrival
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {new Date(shipment.actualArrivalDate).toLocaleDateString()}
                      </Typography>
                    </>
                  )}
                </Box>
              </Stack>
            </Box>

            {/* Status Indicators */}
            {(shipment.isOverdue || shipment.hasDiscrepancy) && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  sx={{ mb: 2, display: 'block' }}
                >
                  Status Indicators
                </Typography>
                <Stack spacing={1}>
                  {shipment.isOverdue && (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'warning.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'warning.200'
                      }}
                    >
                      <Typography variant="body2" color="warning.main" fontWeight="medium">
                        ⚠️ Shipment is overdue
                      </Typography>
                    </Box>
                  )}
                  {shipment.hasDiscrepancy && (
                    <Box
                      sx={{
                        p: 1.5,
                        bgcolor: 'error.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'error.200'
                      }}
                    >
                      <Typography variant="body2" color="error.main" fontWeight="medium">
                        🚨 Discrepancies detected
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            )}

            {/* Additional Info */}
            {shipment.notes && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  sx={{ mb: 1, display: 'block' }}
                >
                  Notes
                </Typography>
                <Typography variant="body2">
                  {shipment.notes}
                </Typography>
              </Box>
            )}

            {/* Files Section */}
            {(shipment.implementationFile || shipment.report) && (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'white',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight="medium"
                  sx={{ mb: 2, display: 'block' }}
                >
                  Files
                </Typography>
                <Stack spacing={1.5}>
                  {shipment.implementationFile && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Assignment sx={{ color: 'primary.main', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Implementation File
                        </Typography>
                      </Stack>
                      <Button
                        onClick={() =>
                          handleFileDownload(
                            shipment.implementationFile!,
                            `${shipment.refNumber}-implementation.xlsx`
                          )
                        }
                        variant="outlined"
                        size="small"
                        startIcon={downloadingFile ? <CircularProgress size={16} /> : <Download />}
                        disabled={downloadingFile}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          minWidth: 'auto'
                        }}
                      >
                        {downloadingFile ? 'Downloading...' : 'Download'}
                      </Button>
                    </Stack>
                  )}

                  {shipment.report && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <InsertDriveFile sx={{ color: 'secondary.main', fontSize: 18 }} />
                        <Typography variant="body2" fontWeight="medium">
                          Report File
                        </Typography>
                      </Stack>
                      <Button
                        onClick={() =>
                          handleFileDownload(
                            shipment.report!,
                            `${shipment.refNumber}-report.xlsx`
                          )
                        }
                        variant="outlined"
                        size="small"
                        startIcon={downloadingFile ? <CircularProgress size={16} /> : <Download />}
                        disabled={downloadingFile}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          minWidth: 'auto'
                        }}
                      >
                        {downloadingFile ? 'Downloading...' : 'Download'}
                      </Button>
                    </Stack>
                  )}
                </Stack>
              </Box>
            )}
          </Stack>
        </CardContent>
      </Card>
    );
  }

  // Compact variant (original layout)
  return (
    <Stack spacing={3}>
      {/* Shipment Details */}
      <Card>
        <CardHeader title="Shipment Details" />
        <CardContent>
          <Stack spacing={2}>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'blue.50',
                border: 1,
                borderColor: 'blue.200'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Person sx={{ color: 'blue.600' }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="blue.600"
                    fontWeight="bold"
                  >
                    Supplier
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {shipment.supplier?.name || shipment.supplierName}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              sx={{
                p: 2,
                bgcolor: 'green.50',
                border: 1,
                borderColor: 'green.200'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <CalendarToday sx={{ color: 'green.600' }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="green.600"
                    fontWeight="bold"
                  >
                    Expected Date
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {new Date(shipment.expectedDate).toLocaleDateString()}
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            <Paper
              sx={{
                p: 2,
                bgcolor: 'orange.50',
                border: 1,
                borderColor: 'orange.200'
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Inventory sx={{ color: 'orange.600' }} />
                <Box>
                  <Typography
                    variant="caption"
                    color="orange.600"
                    fontWeight="bold"
                  >
                    Boxes
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {shipment.receivedBoxCount
                      ? `${shipment.receivedBoxCount} received`
                      : `${shipment.expectedBoxCount} expected`}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Stack>
        </CardContent>
      </Card>

      {/* Enhanced Scanner Panel */}
      {shipment.status === ShipmentStatus.InReception && selectedBox && (
        <Card sx={{ border: 2, borderColor: 'primary.main' }}>
          <CardHeader
            title={
              <Stack direction="row" alignItems="center" spacing={1}>
                <Speed color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  Active Box Scanner
                </Typography>
              </Stack>
            }
            sx={{ bgcolor: 'primary.50' }}
          />
          <CardContent>
            <Paper
              sx={{
                p: 2,
                bgcolor: 'primary.50',
                border: 2,
                borderColor: 'primary.200'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Inventory sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box flex={1}>
                  <Typography
                    variant="caption"
                    color="primary.main"
                    fontWeight="bold"
                  >
                    ACTIVE BOX
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {selectedBox.refNumber}
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="caption" color="text.secondary">
                      {selectedBox.boxItems.length} items
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};

export default ShipmentInfo;